// src/components/Booklet/BookletEditor.js (Complete Updated Version)
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, 
  Eye, 
  Settings, 
  Plus, 
  Link, 
  Video, 
  Volume2, 
  MousePointer,
  Trash2,
  ArrowLeft,
  Globe,
  Share,
  X,
  Play,
  Pause,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookletService } from '../../services/bookletService';
import InteractiveElementModal from './InteractiveElementModal';
import BookletSettings from './BookletSettings';
import toast from 'react-hot-toast';

const BookletEditor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [booklet, setBooklet] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showElementModal, setShowElementModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(null);
  const [showPopupModal, setShowPopupModal] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pageCanvasRef = useRef(null);
  const editorRef = useRef(null);
  const [interactions, setInteractions] = useState({});

  useEffect(() => {
    loadBooklet();
  }, [id, user]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle navigation if no modal is open and no input is focused
      if (showElementModal || showSettings || showVideoModal || showPopupModal) return;
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          goToPreviousPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          goToNextPage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        case 'Escape':
          if (selectedElement) {
            setSelectedElement(null);
            renderPageWithInteractions();
          }
          break;
        case 'Delete':
        case 'Backspace':
          if (selectedElement) {
            e.preventDefault();
            deleteSelectedElement();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPageIndex, pages.length, selectedElement, showElementModal, showSettings, showVideoModal, showPopupModal]);

  const loadBooklet = async () => {
    try {
      setIsLoading(true);
      const bookletData = await bookletService.getBooklet(id);

      if (bookletData.userId !== user.uid) {
        toast.error('Access denied');
        navigate('/booklets');
        return;
      }

      setBooklet(bookletData);
      setPages(bookletData.pages || []);

      // Convert pages to interactions object for easier manipulation
      const interactionsData = {};
      bookletData.pages?.forEach((page, index) => {
        interactionsData[index] = page.interactions || [];
      });
      setInteractions(interactionsData);

    } catch (error) {
      toast.error('Error loading booklet');
      console.error(error);
      navigate('/booklets');
    } finally {
      setIsLoading(false);
    }
  };

  const saveBooklet = async () => {
    try {
      setIsSaving(true);
      await bookletService.updateBooklet(id, {
        title: booklet.title,
        description: booklet.description,
        settings: booklet.settings,
        publishSettings: booklet.publishSettings
      });
      toast.success('Booklet saved successfully');
    } catch (error) {
      toast.error('Error saving booklet');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Updated settings save handler that immediately saves to Firestore
  const handleSettingsSave = async (updates) => {
    try {
      setIsSaving(true);
      
      // Update local state first for immediate UI feedback
      setBooklet(prev => ({ ...prev, ...updates }));
      
      // Save to Firestore immediately
      await bookletService.updateBooklet(id, updates);
      
      toast.success('Settings saved successfully');
      setShowSettings(false);
    } catch (error) {
      toast.error('Error saving settings');
      console.error(error);
      // Optionally reload booklet to reset local state on error
      loadBooklet();
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation functions
  const goToNextPage = () => {
    if (currentPageIndex < pages.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
      setSelectedElement(null);
    }
  };

  const goToPreviousPage = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
      setSelectedElement(null);
    }
  };

  const goToPage = (index) => {
    if (index >= 0 && index < pages.length) {
      setCurrentPageIndex(index);
      setSelectedElement(null);
    }
  };

  // Zoom functions
  const zoomIn = () => {
    setZoom(Math.min(3, zoom + 0.25));
  };

  const zoomOut = () => {
    setZoom(Math.max(0.25, zoom - 0.25));
  };

  const resetZoom = () => {
    setZoom(1);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen && editorRef.current) {
      if (editorRef.current.requestFullscreen) {
        editorRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const addInteractiveElement = async (elementData) => {
    try {
      const interaction = await bookletService.addPageInteraction(id, currentPageIndex, elementData);
      
      setInteractions(prev => ({
        ...prev,
        [currentPageIndex]: [...(prev[currentPageIndex] || []), interaction]
      }));

      renderPageWithInteractions();
      toast.success('Interactive element added');
    } catch (error) {
      toast.error('Error adding interactive element');
      console.error(error);
    }
  };

  const renderPageWithInteractions = () => {
    if (!pageCanvasRef.current || !pages[currentPageIndex]) return;

    const canvas = pageCanvasRef.current;
    const pageInteractions = interactions[currentPageIndex] || [];

    // Clear existing elements
    const existingElements = canvas.querySelectorAll('.interactive-element');
    existingElements.forEach(el => el.remove());

    // Add interactive elements
    pageInteractions.forEach(interaction => {
      const element = createInteractiveElement(interaction);
      canvas.appendChild(element);
    });
  };

  const createInteractiveElement = (interaction) => {
    const element = document.createElement('div');
    element.className = 'interactive-element absolute cursor-pointer transition-all duration-200 hover:scale-105';
    element.style.left = `${interaction.x || 0}%`;
    element.style.top = `${interaction.y || 0}%`;
    element.style.width = `${interaction.width || 10}%`;
    element.style.height = `${interaction.height || 10}%`;
    element.style.zIndex = '20';
    element.setAttribute('data-interaction-id', interaction.id);

    switch (interaction.type) {
      case 'link':
        element.innerHTML = `
          <div class="w-full h-full bg-blue-500 bg-opacity-20 border-2 border-blue-500 border-dashed rounded flex items-center justify-center hover:bg-opacity-30 transition-colors">
            <div class="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
              ${interaction.data?.text || 'Link'}
            </div>
          </div>
        `;
        element.addEventListener('click', (e) => {
          e.stopPropagation();
          if (interaction.data?.url) {
            window.open(interaction.data.url, '_blank');
          }
        });
        break;

      case 'video':
        element.innerHTML = `
          <div class="w-full h-full bg-red-500 bg-opacity-20 border-2 border-red-500 border-dashed rounded flex items-center justify-center hover:bg-opacity-30 transition-colors">
            <div class="bg-red-600 text-white p-2 rounded-full">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        `;
        element.addEventListener('click', (e) => {
          e.stopPropagation();
          openVideoModal(interaction.data);
        });
        break;

      case 'audio':
        element.innerHTML = `
          <div class="w-full h-full bg-green-500 bg-opacity-20 border-2 border-green-500 border-dashed rounded flex items-center justify-center hover:bg-opacity-30 transition-colors">
            <div class="bg-green-600 text-white p-2 rounded-full">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.796L4.828 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.828l3.555-2.796A1 1 0 019.383 3.076zM12 6.658a1 1 0 011.414 0c.914.914 1.414 2.156 1.414 3.414s-.5 2.5-1.414 3.414a1 1 0 01-1.414-1.414c.586-.586.914-1.414.914-2s-.328-1.414-.914-2A1 1 0 0112 6.658z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        `;
        element.addEventListener('click', (e) => {
          e.stopPropagation();
          playAudio(interaction.id, interaction.data);
        });
        break;

      case 'popup':
        element.innerHTML = `
          <div class="w-full h-full bg-purple-500 bg-opacity-20 border-2 border-purple-500 border-dashed rounded flex items-center justify-center hover:bg-opacity-30 transition-colors">
            <div class="bg-purple-600 text-white px-2 py-1 rounded text-xs font-medium">
              ${interaction.data?.title || 'Info'}
            </div>
          </div>
        `;
        element.addEventListener('click', (e) => {
          e.stopPropagation();
          showPopup(interaction.data);
        });
        break;

      default:
        break;
    }

    // Select element on click (for editing)
    element.addEventListener('click', (e) => {
      e.stopPropagation();
      selectElement(interaction.id);
    });

    return element;
  };

  // Video Modal Functions
  const openVideoModal = (videoData) => {
    setShowVideoModal(videoData);
  };

  const closeVideoModal = () => {
    setShowVideoModal(null);
  };

  const getVideoEmbed = (url, autoplay = false, controls = true) => {
    if (!url) return '<p>No video URL provided</p>';
    
    const autoplayParam = autoplay ? '&autoplay=1' : '';
    const controlsParam = controls ? '&controls=1' : '&controls=0';

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = extractYouTubeId(url);
      if (!videoId) return '<p>Invalid YouTube URL</p>';
      return `<iframe width="100%" height="400" src="https://www.youtube.com/embed/${videoId}?${autoplayParam}${controlsParam}" frameborder="0" allowfullscreen></iframe>`;
    } else if (url.includes('vimeo.com')) {
      const videoId = extractVimeoId(url);
      if (!videoId) return '<p>Invalid Vimeo URL</p>';
      return `<iframe width="100%" height="400" src="https://player.vimeo.com/video/${videoId}?${autoplayParam}" frameborder="0" allowfullscreen></iframe>`;
    } else {
      return `<video width="100%" height="400" ${controls ? 'controls' : ''} ${autoplay ? 'autoplay' : ''}><source src="${url}" type="video/mp4">Your browser does not support the video tag.</video>`;
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const extractVimeoId = (url) => {
    const regExp = /vimeo.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // Audio Functions
  const playAudio = (interactionId, audioData) => {
    if (!audioData?.audioUrl) {
      toast.error('No audio URL provided');
      return;
    }

    // Stop all other audio first
    Object.values(audioElements).forEach(audio => {
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    let audio = audioElements[interactionId];
    if (!audio) {
      audio = new Audio(audioData.audioUrl);
      audio.loop = audioData.loop || false;
      setAudioElements(prev => ({ ...prev, [interactionId]: audio }));
    }

    if (audio.paused) {
      audio.play().catch(console.error);
      toast.success('Audio playing');
    } else {
      audio.pause();
      toast.success('Audio paused');
    }
  };

  // Popup Functions
  const showPopup = (popupData) => {
    setShowPopupModal(popupData);
  };

  const closePopup = () => {
    setShowPopupModal(null);
  };

  const selectElement = (interactionId) => {
    setSelectedElement(interactionId);
    // Highlight selected element with better visual feedback
    const elements = pageCanvasRef.current?.querySelectorAll('.interactive-element');
    elements?.forEach(el => {
      if (el.getAttribute('data-interaction-id') === interactionId) {
        el.style.outline = '3px solid #3b82f6';
        el.style.outlineOffset = '2px';
        el.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
      } else {
        el.style.outline = 'none';
        el.style.outlineOffset = '0';
        el.style.boxShadow = 'none';
      }
    });
  };

  const deleteSelectedElement = async () => {
    if (!selectedElement) return;
    
    try {
      await bookletService.deletePageInteraction(id, currentPageIndex, selectedElement);
      
      setInteractions(prev => ({
        ...prev,
        [currentPageIndex]: prev[currentPageIndex].filter(interaction => interaction.id !== selectedElement)
      }));
      
      setSelectedElement(null);
      renderPageWithInteractions();
      toast.success('Interactive element deleted');
    } catch (error) {
      toast.error('Error deleting element');
      console.error(error);
    }
  };

  const publishBooklet = async () => {
    try {
      const publishSettings = {
        isPublic: true,
        allowAnalytics: true
      };
      
      await bookletService.publishBooklet(id, publishSettings);
      toast.success('Booklet published successfully!');
      
      const publicUrl = `${window.location.origin}/booklet/${id}`;
      if (navigator.share) {
        await navigator.share({
          title: booklet.title,
          url: publicUrl
        });
      } else {
        navigator.clipboard.writeText(publicUrl);
        toast.success('Public link copied to clipboard');
      }
    } catch (error) {
      toast.error('Error publishing booklet');
      console.error(error);
    }
  };

  // Improved page styling with zoom support
  const getPageStyle = () => {
    if (!currentPage?.dimensions) {
      return {
        width: `${600 * zoom}px`,
        height: `${800 * zoom}px`,
        maxWidth: 'none',
        maxHeight: 'none'
      };
    }

    const { width = 600, height = 800 } = currentPage.dimensions;
    const baseScale = 0.8; // Base scale for fitting in container
    const finalScale = baseScale * zoom;

    return {
      width: `${width * finalScale}px`,
      height: `${height * finalScale}px`,
      maxWidth: 'none',
      maxHeight: 'none',
      transform: `scale(${zoom})`,
      transformOrigin: 'center center'
    };
  };

  useEffect(() => {
    renderPageWithInteractions();
  }, [currentPageIndex, interactions]);

  // Cleanup audio elements on unmount
  useEffect(() => {
    return () => {
      Object.values(audioElements).forEach(audio => {
        audio.pause();
      });
    };
  }, [audioElements]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 mx-auto animate-spin" />
          <p className="mt-4 text-gray-600">Loading booklet editor...</p>
        </div>
      </div>
    );
  }

  if (!booklet) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Booklet not found</p>
        </div>
      </div>
    );
  }

  const currentPage = pages[currentPageIndex];

  return (
    <div ref={editorRef} className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - Fixed positioning with proper z-index */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 relative z-40 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/booklets')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{booklet.title}</h1>
              <p className="text-sm text-gray-600">
                Page {currentPageIndex + 1} of {pages.length}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1">
              <button
                onClick={zoomOut}
                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-2 text-sm text-gray-600 min-w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={resetZoom}
                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                title="Reset Zoom"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
            
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
            
            <button
              onClick={() => navigate(`/booklet/${id}`)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </button>
            
            <button
              onClick={publishBooklet}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Globe className="w-4 h-4 mr-2" />
              Publish
            </button>
            
            <button
              onClick={saveBooklet}
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area - Proper flex layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Fixed width with proper z-index */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col relative z-30 shadow-lg">
          {/* Page Navigation - Scrollable */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Pages</h3>
            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {pages.map((page, index) => (
                <button
                  key={`page-${index}-${page.pageNumber || index}`}
                  onClick={() => goToPage(index)}
                  className={`
                    relative aspect-[3/4] border-2 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md
                    ${currentPageIndex === index
                      ? 'border-indigo-500 shadow-md ring-2 ring-indigo-200'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <img
                    src={page.thumbnailUrl || page.url}
                    alt={`Page ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs text-center py-1">
                    {index + 1}
                  </div>
                  {interactions[index]?.length > 0 && (
                    <div className="absolute top-1 right-1 w-3 h-3 bg-indigo-500 rounded-full border-2 border-white"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Elements Tools - Scrollable */}
          <div className="p-4 flex-1 overflow-y-auto">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Add Interactive Elements</h3>
            <div className="space-y-2">
              {[
                { type: 'link', icon: Link, label: 'Link', description: 'Add clickable links', color: 'text-blue-600' },
                { type: 'video', icon: Video, label: 'Video', description: 'Embed videos', color: 'text-red-600' },
                { type: 'audio', icon: Volume2, label: 'Audio', description: 'Add audio clips', color: 'text-green-600' },
                { type: 'popup', icon: MousePointer, label: 'Popup', description: 'Show text popups', color: 'text-purple-600' }
              ].map((tool) => {
                const IconComponent = tool.icon;
                return (
                  <button
                    key={tool.type}
                    onClick={() => setShowElementModal(tool.type)}
                    className="w-full flex items-center space-x-3 p-3 text-left text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors hover:shadow-sm"
                  >
                    <IconComponent className={`w-5 h-5 ${tool.color}`} />
                    <div>
                      <div className="font-medium">{tool.label}</div>
                      <div className="text-xs text-gray-500">{tool.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected Element Controls */}
            {selectedElement && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Element</h4>
                <button
                  onClick={deleteSelectedElement}
                  className="w-full flex items-center justify-center space-x-2 p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Element</span>
                </button>
              </div>
            )}

            {/* Keyboard Shortcuts Help */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Keyboard Shortcuts</h4>
              <div className="text-xs text-gray-600 space-y-1">
                <div>← → : Navigate pages</div>
                <div>+ - : Zoom in/out</div>
                <div>0 : Reset zoom</div>
                <div>Del : Delete selected</div>
                <div>Esc : Deselect</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Page Editor with navigation and proper overflow */}
        <div className="flex-1 flex flex-col relative z-10">
          {/* Page Navigation Controls */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
            <button
              onClick={goToPreviousPage}
              disabled={currentPageIndex === 0}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="text-sm text-gray-600">
              Page {currentPageIndex + 1} of {pages.length}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPageIndex === pages.length - 1}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Page Content with zoom support */}
          <div className="flex-1 flex items-center justify-center p-6 bg-gray-100 overflow-auto">
            {currentPage && (
              <div className="relative flex items-center justify-center">
                {/* Navigation Arrows */}
                {currentPageIndex > 0 && (
                  <button
                    onClick={goToPreviousPage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-white shadow-lg hover:shadow-xl rounded-full transition-all duration-200 border border-gray-200"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                  </button>
                )}

                {currentPageIndex < pages.length - 1 && (
                  <button
                    onClick={goToNextPage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-white shadow-lg hover:shadow-xl rounded-full transition-all duration-200 border border-gray-200"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-600" />
                  </button>
                )}

                <div
                  ref={pageCanvasRef}
                  className="relative bg-white shadow-2xl rounded-lg overflow-hidden border border-gray-200"
                  style={getPageStyle()}
                  onClick={() => setSelectedElement(null)}
                >
                  <img
                    src={currentPage.url}
                    alt={`Page ${currentPageIndex + 1}`}
                    className="w-full h-full object-contain pointer-events-none select-none"
                    draggable={false}
                  />
                  {/* Interactive elements will be rendered here by renderPageWithInteractions */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals with highest z-index to ensure they appear above everything */}
      <InteractiveElementModal
        isOpen={!!showElementModal}
        type={showElementModal}
        onClose={() => setShowElementModal(false)}
        onSave={addInteractiveElement}
      />

      <BookletSettings
        isOpen={showSettings}
        booklet={booklet}
        onClose={() => setShowSettings(false)}
        onSave={handleSettingsSave}
        isSaving={isSaving}
      />

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={closeVideoModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <div 
              className="bg-black rounded-lg overflow-hidden"
              dangerouslySetInnerHTML={{
                __html: getVideoEmbed(showVideoModal.videoUrl, showVideoModal.autoplay, showVideoModal.controls)
              }}
            />
          </div>
        </div>
      )}

      {/* Popup Modal */}
      {showPopupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">{showPopupModal.title}</h3>
              <button
                onClick={closePopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="prose max-w-none">
                {showPopupModal.content?.split('\n').map((line, index) => (
                  <p key={`popup-line-${index}`} className="mb-2">{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookletEditor;